import localTools from '../data/tools.json';

const toStringSafe = (value) => String(value || '').trim();

const toIdentityParts = (tool = {}) => ({
  slug: toStringSafe(tool?.slug).toLowerCase(),
  id: toStringSafe(tool?.id).toLowerCase(),
  name: toStringSafe(tool?.name).toLowerCase(),
});

const byIdentity = (tool) => {
  const { slug, id, name } = toIdentityParts(tool);
  if (slug) return `slug:${slug}`;
  if (id) return `id:${id}`;
  if (name) return `name:${name}`;
  return `${slug}::${id}`;
};

const normalizeCmsTool = (tool = {}) => {
  const affiliate = tool.affiliate_link || {};
  return {
    ...tool,
    id: tool.id,
    slug: tool.slug,
    status: tool.status || (affiliate.my_referral_link ? 'approved' : 'pending'),
    affiliate_url:
      tool.affiliate_url ||
      affiliate.my_referral_link ||
      affiliate.signup_url ||
      affiliate.url ||
      '',
    direct_url: tool.direct_url || tool.website_url || '',
    featured: Boolean(tool.is_featured || tool.featured),
  };
};

export const mergeTools = (baseTools = [], cmsTools = []) => {
  const cmsBySlug = new Map();
  const cmsById = new Map();

  cmsTools.forEach((entry) => {
    const normalized = normalizeCmsTool(entry);
    const slug = toStringSafe(normalized.slug).toLowerCase();
    const id = toStringSafe(normalized.id).toLowerCase();

    if (slug) cmsBySlug.set(slug, normalized);
    if (id) cmsById.set(id, normalized);
  });

  return baseTools.map((base) => {
    const slug = toStringSafe(base.slug).toLowerCase();
    const id = toStringSafe(base.id).toLowerCase();
    const cmsMatch = cmsBySlug.get(slug) || cmsById.get(id);

    if (!cmsMatch) {
      return {
        ...base,
        featured: Boolean(base.featured),
      };
    }

    return {
      ...base,
      ...cmsMatch,
      affiliate_url: cmsMatch.affiliate_url || base.affiliate_url || '',
      status: cmsMatch.status || base.status || 'pending',
      featured: Boolean(cmsMatch.featured || base.featured),
    };
  });
};

export const getTools = async ({ fetchCmsTools, limit } = {}) => {
  const localList = Array.isArray(localTools) ? localTools : [];
  let cmsList = [];

  if (typeof fetchCmsTools === 'function') {
    try {
      const result = await fetchCmsTools();
      cmsList = Array.isArray(result) ? result : [];
    } catch {
      cmsList = [];
    }
  }

  const merged = mergeTools(localList, cmsList);

  if (typeof limit === 'number' && limit > 0) {
    return merged.slice(0, limit);
  }

  return merged;
};

export const mergeAndAppendCmsTools = (mergedLocalTools = [], cmsTools = []) => {
  const existing = new Set(mergedLocalTools.map(byIdentity));
  const existingSlugs = new Set();
  const existingIds = new Set();

  mergedLocalTools.forEach((tool) => {
    const { slug, id } = toIdentityParts(tool);
    if (slug) existingSlugs.add(slug);
    if (id) existingIds.add(id);
  });

  const appended = cmsTools
    .map(normalizeCmsTool)
    .filter((item) => {
      const key = byIdentity(item);
      const { slug, id } = toIdentityParts(item);
      if (existing.has(key)) return false;
      if (slug && existingSlugs.has(slug)) return false;
      if (id && existingIds.has(id)) return false;

      existing.add(key);
      if (slug) existingSlugs.add(slug);
      if (id) existingIds.add(id);
      return true;
    });

  return [...mergedLocalTools, ...appended];
};
